import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EvaluagentAPIService } from '@app/domain/evaluagent/api/evaluagent-api.service';
import { EvaluagentToken, UpdateEvaluagentToken } from '@app/domain/evaluagent/model/evaluagent-token.model';
import { Breadcrumb } from '@app/models/breadcrumb.model';
import { IState } from '@app/models/state/state.model';
import { BreadcrumbService } from '@app/shared/breadcrumbs/breadcrumbs.service';
import { ButtonType } from '@app/shared/components/inputs/button/button.component';
import { PillType } from '@app/shared/components/pill/pill.component';
import { NotifyUtils } from '@app/shared/utils/notify.utils';

interface PageState extends IState {
  isConfiguring: boolean;

  submitted: boolean;
  submitting: boolean;
}

@Component({
  selector: 'app-integration-evaluagent',
  templateUrl: './integration-evaluagent.component.html',
  styleUrls: ['./integration-evaluagent.component.scss'],
})
export class IntegrationEvaluagentComponent implements OnInit, OnDestroy {
  public readonly eButtonType = ButtonType;
  public readonly ePillType = PillType;

  state: PageState;
  breadcrumb: Breadcrumb;

  integrationSetupForm: FormGroup;
  currentToken: EvaluagentToken | null;

  get controlApiKey(): FormControl {
    return this.integrationSetupForm.controls.apiKey as FormControl;
  }

  get controlApiSecret(): FormControl {
    return this.integrationSetupForm.controls.apiSecret as FormControl;
  }

  constructor(
    public route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private evaluagentAPIService: EvaluagentAPIService,
    private notifyUtils: NotifyUtils,
  ) {
    this.currentToken = undefined;
    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
      isConfiguring: false,
      submitted: false,
      submitting: false
    };

    this.breadcrumb = this.breadcrumbService.init(this.route);
    this.integrationSetupForm = this.initFormSetup();
  }

  ngOnInit(): void {
    this.getData();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  getData(): void {
    this.evaluagentAPIService.getCredentials()
      .toPromise()
      .then(token => {
        this.populatePageFromToken(token);
      })
      .finally(() => {
        this.state.loading = false;
      });
  }

  initFormSetup(token?: EvaluagentToken): FormGroup {
    const formGroup = new FormGroup({
      apiKey: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      apiSecret: new FormControl('', [Validators.required, Validators.maxLength(255)])
    });

    if (token) {
      formGroup.controls.apiKey.setValue(token.apiKey);
      // formGroup.controls.apiSecret.setValue(token.apiSecret);
    }

    return formGroup;
  }

  saveCredentials(): void {
    this.state.submitted = true;

    if (this.state.submitting) { return console.error('Form is already submitting'); }
    if (this.integrationSetupForm.invalid) { return console.error('Form is invalid'); }
    this.state.submitting = true;

    const updateRequest: UpdateEvaluagentToken = {
      apiKey: this.controlApiKey.value.trim(),
      apiSecret: this.controlApiSecret.value.trim()
    };

    this.evaluagentAPIService.updateCredentials(updateRequest)
      .toPromise()
      .then(token => {
        this.populatePageFromToken(token);
        this.state.submitted = false;
        this.state.isConfiguring = false;
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  populatePageFromToken(token: EvaluagentToken): void {
    this.integrationSetupForm = this.initFormSetup(token);
    this.currentToken = token;
  }

  startSetup(): void {
    this.state.isConfiguring = true;
  }

  cancelSetup(): void {
    this.state.isConfiguring = false;
  }

  removeToken(): void {
    if (this.state.submitting) { return; }
    this.state.submitting = true;

    this.evaluagentAPIService.removeCredentials()
      .toPromise()
      .then(() => {
        this.getData();
      })
      .finally(() => {
        this.state.submitting = false;
      });
  }

  runManualSync(): void {
    if (!this.currentToken) { return; }
    if (this.state.submitting) { return; }
    this.state.submitting = true;

    this.evaluagentAPIService.runManualSync()
      .toPromise()
      .then(() => {
        this.currentToken.syncing = true;
        this.notifyUtils.notify('Manual sync started');
        // After 2 minutes, refresh the page
        setTimeout(() => {
          this.state.submitting = false;
          this.state.loading = true;
          this.getData();
        }, 120000);
      });
  }
}
